import axios, { AxiosError } from "axios";
import { useQuery } from "react-query";
import { API_URL } from "../consts";
import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import { Button, DialogActions, DialogContent, TextField } from "@mui/material";
import { Order, stableSort, getComparator } from "../ui/Tables";
import { oraPrenotaStore } from "../oraPrenotaStore";
import { useState } from "react";
import { Loading } from "../ui/Loading";
import { EuroFormatter } from "../utils/EuroFormatter";
import { TransazioneApi } from "./Transazione";
import { format } from "date-fns";
import { readDate } from "../utils/dateUtils";
import * as ExcelJS from "exceljs";
import { downloadExcel } from "../utils/excel";
import { CardsData } from "../prenotazione/CardsData";

interface HeadCell {
  disablePadding: boolean;
  id: keyof TransazioneApi;
  label: string;
  numeric: boolean;
  key: string;
  filter: boolean;
  width: string;
}

const headCells: readonly HeadCell[] = [
  {
    id: "dataTransazione",
    numeric: false,
    disablePadding: false,
    label: "Data",
    key: "dataTransazione",
    filter: true,
    width: "100px",
  },
  {
    id: "importo",
    numeric: true,
    disablePadding: false,
    label: "Importo",
    key: "importo",
    filter: true,
    width: "80px",
  },
  {
    id: "causale",
    numeric: false,
    disablePadding: false,
    label: "Causale",
    key: "causale",
    filter: true,
    width: "200px",
  },
  {
    id: "codiceCard",
    numeric: false,
    disablePadding: false,
    label: "Codice Card",
    key: "codiceCard",
    filter: true,
    width: "90px",
  },
  {
    id: "cognome",
    numeric: false,
    disablePadding: false,
    label: "Nominativo",
    key: "cognome",
    filter: true,
    width: "100px",
  },
  {
    id: "saldoCorrente",
    numeric: false,
    disablePadding: false,
    label: "Saldo Conto",
    key: "saldoCorrente",
    filter: true,
    width: "80px",
  },
  {
    id: "id",
    numeric: true,
    disablePadding: false,
    label: "Id",
    key: "id",
    filter: true,
    width: "30px",
  },
];

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof TransazioneApi) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  onFilter: (event: any, property: keyof TransazioneApi, filter: string | number) => void;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort, onFilter } = props;
  const createSortHandler =
    (property: keyof TransazioneApi) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };
  const createFilterHandler = (property: keyof TransazioneApi) => (event: any) => {
    onFilter(event, property, event.target.value);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell key={headCell.key ? headCell.key : headCell.id}>
            {headCell.filter && (
              <TextField
                label=""
                variant="standard"
                onChange={createFilterHandler(headCell.id)}
                sx={{ maxWidth: headCell.width }}
              />
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export function Transazioni(props: { idConto: number; close: () => void; conto: CardsData }) {
  const { idConto, close, conto } = props;

  const { isLoading, data } = useQuery<{ transazioni: TransazioneApi[] }, AxiosError>(
    ["cards/findTransazioni", idConto],
    () => axios.get(API_URL + "cards/findTransazioni/" + idConto).then((res) => res.data)
  );

  const rows = data?.transazioni ?? [];

  const [order, setOrder] = React.useState<Order>("desc");
  const [orderBy, setOrderBy] = React.useState<keyof TransazioneApi>("id");
  const [page, setPage] = React.useState(0);
  const dense = oraPrenotaStore((state) => state.tabelleCompatte);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof TransazioneApi) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const [filters, setFilters] = useState({});
  const handleFilterRequest = (
    event: React.MouseEvent<unknown>,
    property: keyof TransazioneApi,
    filter: string | number
  ) => {
    setFilters({
      ...filters,
      [property]: filter,
    });
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  if (isLoading) return <Loading />;

  const transazioni = stableSort(rows, getComparator(order, orderBy), filters);

  const exportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Transazioni");
    sheet.columns = [
      {
        header: "Data Transazione",
        key: "dataTransazione",
        width: 15,
        style: { numFmt: "dd/mm/yyyy" },
      },
      {
        header: "Importo",
        key: "importo",
        width: 15,
        style: { numFmt: "#,##0.00" },
      },
      { header: "Causale", key: "causale", width: 40 },
      { header: "Codice Card", key: "codiceCard", width: 20 },
      { header: "Nome", key: "nome", width: 30 },
      { header: "Cognome", key: "cognome", width: 30 },
      {
        header: "Saldo Corrente",
        key: "saldoCorrente",
        width: 15,
        style: { numFmt: "#,##0.00" },
      },
      { header: "Id", key: "id", width: 15 },
    ];

    transazioni.forEach((row) =>
      sheet.addRow({
        dataTransazione: readDate(row.dataTransazione),
        importo: row.importo,
        causale: row.causale,
        codiceCard: row.codiceCard,
        nome: row.nome,
        cognome: row.cognome,
        saldoCorrente: row.saldoCorrente,
        id: row.id,
      })
    );

    const buff = await workbook.xlsx.writeBuffer();
    downloadExcel(buff, "transazioni_" + conto.codiceConto + ".xlsx");
  };

  return (
    <>
      <DialogContent>
        <Box sx={{ width: "100%" }}>
          <TableContainer>
            <Table aria-labelledby="tableTitle" size={dense ? "small" : "medium"}>
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={rows.length}
                onFilter={handleFilterRequest}
              />
              <TableBody classes={{ root: "table-body" }}>
                {transazioni
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row: TransazioneApi, index) => {
                    return (
                      <TableRow hover tabIndex={-1} key={row.id}>
                        <TableCell>{format(readDate(row.dataTransazione), "dd/MM/yyyy")}</TableCell>
                        <TableCell align="right">{EuroFormatter.format(row.importo)}</TableCell>
                        <TableCell>{row.causale}</TableCell>
                        <TableCell>{row.codiceCard}</TableCell>
                        <TableCell>
                          {row.cognome} {row.nome}
                        </TableCell>
                        <TableCell align="right">
                          {row.saldoCorrente !== null && EuroFormatter.format(row.saldoCorrente)}
                        </TableCell>
                        <TableCell align="right">{row.id}</TableCell>
                      </TableRow>
                    );
                  })}
                {emptyRows > 0 && (
                  <TableRow
                    style={{
                      height: (dense ? 33 : 53) * emptyRows,
                    }}
                  >
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button color="success" onClick={() => exportExcel()}>
          Excel
        </Button>
        <Button onClick={() => close()}>Chiudi</Button>
      </DialogActions>
    </>
  );
}
